/**
 * Turn an axios error (or any thrown value) into a human-friendly message.
 *
 * Handles the shapes our backends actually send:
 * - `{ code, msg }` / `{ code, message }`            -> "[code] msg"
 * - a raw JSON string body (wrong content-type)       -> parsed, then as above
 * - gardener wrapping an upstream error as
 *   `{ code: <httpStatus>, msg: "<raw upstream body>" }` where the raw body is
 *   itself `{ code, msg }`                             -> unwrapped to the inner code/msg
 * - a non-empty plain string body                      -> the string
 * - a deliberately-thrown non-axios `Error`            -> its message
 * - anything else                                      -> the caller's `fallback`
 *
 * The caller's `fallback` (e.g. "Failed to create promotion") intentionally wins over
 * axios's generic `error.message` ("Request failed with status code 500"): a screen-specific
 * message is more useful than the transport-level one when the server sent no usable body.
 *
 * Use this everywhere an API error is shown (global popup, inline form errors)
 * so the user never sees a raw `{"code":40011,"msg":"..."}` blob.
 */

/** If a value is a JSON object string like `{"code":..,"msg":..}`, parse it; otherwise return null. */
const tryParseErrorObject = (value: unknown): Record<string, any> | null => {
    if (typeof value !== 'string') return null;
    const trimmed = value.trim();
    if (!trimmed.startsWith('{')) return null;
    try {
        const parsed = JSON.parse(trimmed);
        return parsed && typeof parsed === 'object' ? parsed : null;
    } catch {
        return null;
    }
};

export const extractApiErrorMessage = (error: any, fallback = 'Request failed'): string => {
    // A non-axios Error carries a message the caller wrote deliberately (e.g. `throw new Error(...)`),
    // so keep it. Axios errors (isAxiosError) hold only a transport-level message and fall through to
    // response parsing / fallback.
    if (error instanceof Error && !(error as any).isAxiosError && error.message) return error.message;

    let data = error?.response?.data;
    // Some responses arrive as a raw JSON string (e.g. wrong content-type) instead of a parsed object.
    const parsedTop = tryParseErrorObject(data);
    if (parsedTop) data = parsedTop;
    if (typeof data === 'string') {
        const text = data.trim();
        // A non-empty plain string is the message; but an HTML error page (e.g. a 502 from the
        // gateway) is not user-facing text — fall back instead of dumping markup into a popup.
        return text && !text.startsWith('<') ? text : fallback;
    }

    if (data && typeof data === 'object') {
        let code = data.code ?? data.errorCode;
        let msg = data.msg ?? data.message;
        // gardener wraps an upstream (payment-service) error as { code: <httpStatus>, msg: "<raw body>" },
        // where the raw body is itself { code, msg }. Unwrap it so we surface the real business code/msg
        // (e.g. 40011 / "Expired promotion code") instead of the HTTP status + a JSON blob.
        const inner = tryParseErrorObject(msg);
        if (inner && (inner.msg || inner.message)) {
            msg = inner.msg ?? inner.message;
            code = inner.code ?? inner.errorCode ?? code;
        }
        // Only treat text-like msg as a message; an object msg would stringify to "[object Object]".
        if (typeof msg === 'string' || typeof msg === 'number') {
            const text = String(msg);
            // Show as "[40011] Expired promotion code" when a code is present.
            return code === undefined || code === null ? text : `[${code}] ${text}`;
        }
    }
    return fallback;
};
